import { Typography } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { Stack } from '@mui/system';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import MetricsApi from '../../api/MetricsApi';
import { AuthContext, AuthData } from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { ErrorContext, ErrorData } from '../../context/ErrorProvider';
import { ClaimsDataCard } from './ClaimsDataCard';
import {
	DASHBOARD_POLICYHOLDER_TABLE_FF,
	DASHBOARD_POLICYHOLDER_TABLE_TITLE,
} from './constants';
import { FailedMetricsCard } from './FailedMetricsCard';
import { InvoiceDataCard } from './InvoiceDataCard';
import { PolicyDataCard } from './PolicyDataCard';
import PolicyholdersTable from './PolicyholdersTable';
import { ReadOnlyMetricsCard } from './ReadOnlyMetricsCard';

interface Props {
	configData: ConfigData;
	authData: AuthData;
	errorData: ErrorData;
}

const Dashboard = (props: Props) => {
	const [loading, setLoading] = useState(true);
	const [metrics, setMetrics] = useState(null);
	const { authData, errorData } = props;
	const { hasPermission } = authData;
	const { configProperties } = authData.auth;
	const policyholderTableEnabled =
		configProperties[DASHBOARD_POLICYHOLDER_TABLE_FF];

	useEffect(() => {
		const source = axios.CancelToken.source();

		const fetchMetrics = async () => {
			try {
				setLoading(true);
				const metricData = await MetricsApi.fetchTenantMetrics(
					authData.auth.authorizationToken,
					{ cancelToken: source.token },
				);
				setMetrics(metricData.data);
				setLoading(false);
				errorData.setCurrentErrorObject(null);
			} catch (error) {
				if (axios.isCancel(error)) {
					return;
				} else {
					errorData.setCurrentErrorObject(error);
					setLoading(false);
				}
			}
		};
		if (hasPermission('analytics.fetch')) {
			fetchMetrics();
		} else {
			setLoading(false);
		}

		return () => {
			source.cancel();
		};
	}, []);

	const renderMetrics = () => {
		if (!hasPermission('analytics.fetch')) {
			return <ReadOnlyMetricsCard />;
		} else if (errorData?.currentErrorObject?.response?.status) {
			return <FailedMetricsCard />;
		}

		const stats = metrics ? metrics.stats : ({} as any);

		return (
			<>
				<PolicyDataCard data={stats} />
				<ClaimsDataCard data={stats} />
				<InvoiceDataCard data={stats} />
			</>
		);
	};

	const renderMetricSkeleton = () => {
		return (
			<>
				<Skeleton
					variant='rectangular'
					sx={{ width: { xs: 'auto', sm: 1 / 3 }, height: 150 }}
				/>
				<Skeleton
					variant='rectangular'
					sx={{ width: { xs: 'auto', sm: 1 / 3 }, height: 150 }}
				/>
				<Skeleton
					variant='rectangular'
					sx={{ width: { xs: 'auto', sm: 1 / 3 }, height: 150 }}
				/>
			</>
		);
	};

	const renderContent = () => {
		return (
			<Stack spacing={2} height='100%'>
				<Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
					{loading ? renderMetricSkeleton() : renderMetrics()}
				</Stack>
				{policyholderTableEnabled && (
					<>
						<Typography variant='h3'>
							{DASHBOARD_POLICYHOLDER_TABLE_TITLE}
						</Typography>
						<PolicyholdersTable
							policyholderCount={metrics?.stats?.count_policyholders ?? 0}
						/>
					</>
				)}
			</Stack>
		);
	};

	return <>{renderContent()}</>;
};

function withContext(Component: any) {
	return (props: Omit<Props, 'configData' | 'authData' | 'errorData'>) => (
		<ErrorContext.Consumer>
			{(errorData) => (
				<ConfigContext.Consumer>
					{(configData) => (
						<AuthContext.Consumer>
							{(authData) => (
								<Component
									{...props}
									configData={configData}
									authData={authData}
									errorData={errorData}
								/>
							)}
						</AuthContext.Consumer>
					)}
				</ConfigContext.Consumer>
			)}
		</ErrorContext.Consumer>
	);
}

export default withContext(Dashboard);
